/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React from 'react';
import { Paper, Typography } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import type { ROCCurveData } from '../../../types';

interface Props {
  data: ROCCurveData;
  title?: string;
}

export default function ROCCurve({ data, title = 'ROC Curve' }: Props) {
  const chartData = data.fpr.map((fpr, i) => ({
    fpr: Math.round(fpr * 1000) / 1000,
    tpr: Math.round(data.tpr[i] * 1000) / 1000,
  }));

  // Approximate AUC using trapezoidal rule
  let auc = 0;
  for (let i = 1; i < data.fpr.length; i++) {
    auc += (data.fpr[i] - data.fpr[i - 1]) * (data.tpr[i] + data.tpr[i - 1]) / 2;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title} (AUC: {auc.toFixed(4)})
      </Typography>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 25, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="fpr"
            type="number"
            domain={[0, 1]}
            label={{ value: 'False Positive Rate', position: 'bottom', offset: 10 }}
          />
          <YAxis
            type="number"
            domain={[0, 1]}
            label={{ value: 'True Positive Rate', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip />
          <ReferenceLine
            segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]}
            stroke="#ccc"
            strokeDasharray="5 5"
          />
          <Line
            type="monotone"
            dataKey="tpr"
            stroke="#1976d2"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
}
